/**
 * Cockpit — the dispatcher’s single-page command center.
 * Zones: action queue (left), day-by-hour calendar (center), context panel (right).
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useGet, apiPost, apiDelete } from '../../hooks/useApi.js';
import { CardSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState } from '../../components/ui/EmptyState.js';
import { relativeTime } from '../../lib/format.js';
import { WeekGrid, MonthGrid } from './CockpitViews.js';
import { TicketDetailPage } from './TicketDetailPage.js';
import { toast } from 'sonner';

function centralOffset() {
  const now = new Date();
  const utc = new Date(now.getTime() + now.getTimezoneOffset() * 60000);
  const central = new Date(utc.toLocaleString('en-US', { timeZone: 'America/Chicago' }));
  return Math.round((utc.getTime() - central.getTime()) / 60000);
}

interface DispatchCandidate {
  techId: string; techName: string; score: number; skillScore?: number;
  availabilityScore?: number; workloadScore?: number; reason?: string; rank: number;
  proposedStart?: string; proposedEnd?: string;
}
interface DispatchDecision {
  id: string; ticketId: string; ticketSummary: string; board: string;
  mode: 'shadow' | 'recommend' | 'auto'; createdAt: string;
  candidates: DispatchCandidate[]; chosenTechId: string | null; overriddenTo: string | null;
  ticketCacheId?: string;
}
interface ScheduleBlock { technicianId: string; kind: string; startsAt: string; endsAt: string; }
interface OnCallSlot { id: string; techId: string; techName: string; startsAt: string; endsAt: string; rotation: string; }
interface ScheduleTech {
  techId: string; name: string; role: string; avatarInitials: string;
  openTickets: number; weekBookedHours: number; capacityHours: number; utilization: number;
}
interface ScheduleResponse {
  items: ScheduleTech[];
  blocks: ScheduleBlock[];
  sourceStatus: string;
}

const BLOCK_COLORS: Record<string, string> = {
  ticket: 'bg-blue-500/80 border-blue-600',
  appointment: 'bg-brand-500/80 border-brand-600',
  pto: 'bg-gray-400/70 border-gray-500',
  admin: 'bg-purple-500/70 border-purple-600',
  training: 'bg-teal-500/70 border-teal-600',
};

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

function modeBadge(mode: string): string {
  if (mode === 'auto') return 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300';
  if (mode === 'recommend') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
}

function rankBadge(rank: number): string {
  return rank === 1
    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
    : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300';
}

export function CockpitPage() {
  const navigate = useNavigate();
  const [dayOffset, setDayOffset] = React.useState(0);
  const [viewMode, setViewMode] = React.useState<'day' | 'week' | 'month'>('day');
  const [boardFilter, setBoardFilter] = React.useState<Set<string>>(new Set());
  const [boardMenuOpen, setBoardMenuOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<DispatchDecision | null>(null);
  const [selectedTech, setSelectedTech] = React.useState<{ techId: string; name: string } | null>(null);
  const [techFilter, setTechFilter] = React.useState<Set<string>>(new Set());
  const [techMenuOpen, setTechMenuOpen] = React.useState(false);
  const [savedViews, setSavedViews] = React.useState<Array<{ name: string; techIds: string[] }>>([]);
  const [viewName, setViewName] = React.useState('');
  const techFilterKey = 'cockpit.techFilter';
  const techViewsKey = 'cockpit.techViews';
  React.useEffect(() => {
    try {
      const f = sessionStorage.getItem(techFilterKey);
      if (f) setTechFilter(new Set(JSON.parse(f) as string[]));
      const v = localStorage.getItem(techViewsKey);
      if (v) setSavedViews(JSON.parse(v) as Array<{ name: string; techIds: string[] }>);
    } catch { /* best-effort */ }
  }, []);
  function persistTechFilter(next: Set<string>) {
    setTechFilter(next);
    try { sessionStorage.setItem(techFilterKey, JSON.stringify([...next])); } catch { /* best-effort */ }
  }
  function saveCurrentView() {
    const name = viewName.trim();
    if (!name) return;
    const next = [...savedViews.filter(v => v.name !== name), { name, techIds: [...techFilter] }];
    setSavedViews(next);
    try { localStorage.setItem(techViewsKey, JSON.stringify(next)); } catch { /* best-effort */ }
    setViewName('');
    toast.success('View "' + name + '" saved');
  }
  function applySavedView(v: { name: string; techIds: string[] }) {
    persistTechFilter(new Set(v.techIds));
    setTechMenuOpen(false);
  }
  function deleteSavedView(name: string) {
    const next = savedViews.filter(v => v.name !== name);
    setSavedViews(next);
    try { localStorage.setItem(techViewsKey, JSON.stringify(next)); } catch { /* best-effort */ }
  }
  const [boardViews, setBoardViews] = React.useState<Array<{ name: string; boards: string[] }>>([]);
  const [boardViewName, setBoardViewName] = React.useState('');
  const boardViewsKey = 'cockpit.boardViews';
  React.useEffect(() => {
    try {
      const v = localStorage.getItem(boardViewsKey);
      if (v) setBoardViews(JSON.parse(v) as Array<{ name: string; boards: string[] }>);
    } catch { /* best-effort */ }
  }, []);
  function saveBoardView() {
    const name = boardViewName.trim();
    if (!name) return;
    const next = [...boardViews.filter(v => v.name !== name), { name, boards: [...boardFilter] }];
    setBoardViews(next);
    try { localStorage.setItem(boardViewsKey, JSON.stringify(next)); } catch { /* best-effort */ }
    setBoardViewName('');
    toast.success('Board view "' + name + '" saved');
  }
  function applyBoardView(v: { name: string; boards: string[] }) {
    setBoardFilter(new Set(v.boards));
    setBoardMenuOpen(false);
  }
  function deleteBoardView(name: string) {
    const next = boardViews.filter(v => v.name !== name);
    setBoardViews(next);
    try { localStorage.setItem(boardViewsKey, JSON.stringify(next)); } catch { /* best-effort */ }
  }
  const { data: techSkillsData } = useGet<{ items: Array<{ id: string; name: string; skills: Array<{ skillId: string; skillName: string; category: string; level: number; source: string }> }> }>('/api/v1/technicians');
  const [accepting, setAccepting] = React.useState<string | null>(null);
  const [accepted, setAccepted] = React.useState<Set<string>>(new Set());
  const [dropBusy, setDropBusy] = React.useState<string | null>(null);

  const { data: dispatchData, loading: dispatchLoading, refetch: refetchDispatch } =
    useGet<{ items: DispatchDecision[]; pending: number }>('/api/v1/dispatch?limit=50');

  const viewDate = new Date();
  viewDate.setDate(viewDate.getDate() + dayOffset);
  const weekStart = new Date(viewDate);
  weekStart.setDate(weekStart.getDate() - ((weekStart.getDay() + 6) % 7));
  const weekStartStr = weekStart.toISOString().slice(0, 10);
  const { data: schedData, loading: schedLoading, refetch: refetchSchedule } =
    useGet<ScheduleResponse>('/api/v1/schedule?weekStart=' + weekStartStr + '&tz=' + centralOffset());
  const { data: oncallData, refetch: refetchOncall } = useGet<{ items: OnCallSlot[] }>('/api/v1/oncall');
  const oncallByTech = new Map((oncallData?.items ?? []).map(s => [s.techId, s]));
  function isOnCallDuring(techId: string, dayStart: Date, dayEnd: Date): boolean {
    const slot = oncallByTech.get(techId);
    if (!slot) return false;
    return new Date(slot.startsAt) < dayEnd && new Date(slot.endsAt) > dayStart;
  }
  const dayStart = new Date(viewDate); dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart.getTime() + 86400000);
  const [oncallMenuOpen, setOncallMenuOpen] = React.useState(false);
  const dayOncallSlots = (oncallData?.items ?? []).filter(s => new Date(s.startsAt) < dayEnd && new Date(s.endsAt) > dayStart);
  const primaryOncall = dayOncallSlots.find(s => s.rotation === 'Primary') ?? dayOncallSlots[0];
  const wkEndH = new Date(weekStart.getTime() + 7 * 86400000);
  const weekOncallSlots = (oncallData?.items ?? []).filter(s => new Date(s.startsAt) < wkEndH && new Date(s.endsAt) > weekStart);
  const weekPrimary = weekOncallSlots.find(s => s.rotation === 'Primary') ?? weekOncallSlots[0];
  const headerOncall = viewMode === 'week' ? weekPrimary : primaryOncall;
  async function assignOncall(techId: string) {
    try {
      const wkStart2 = new Date(weekStart); wkStart2.setHours(0, 0, 0, 0);
      const wkEnd2 = new Date(wkStart2.getTime() + 7 * 86400000);
      const weekSlots = (oncallData?.items ?? []).filter(s => new Date(s.startsAt) < wkEnd2 && new Date(s.endsAt) > wkStart2);
      for (const s of weekSlots) { try { await apiDelete('/api/v1/oncall/' + s.id); } catch { /* best-effort */ } }
      const wkStart = new Date(weekStart); wkStart.setHours(0, 0, 0, 0); const wkEnd = new Date(wkStart.getTime() + 7 * 86400000);
      await apiPost('/api/v1/oncall', { techId, startsAt: wkStart.toISOString(), endsAt: wkEnd.toISOString(), rotation: 'Primary' });
      toast.success('On-call set for ' + viewDate.toLocaleDateString('en-US', { timeZone: 'America/Chicago' }));
      setOncallMenuOpen(false);
      refetchOncall();
    } catch (e) { toast.error(e instanceof Error ? e.message : 'Failed to set on-call'); }
  }


  const decisions = (dispatchData?.items ?? []).filter(d => !d.chosenTechId && !d.overriddenTo);
  const availableBoards = Array.from(new Set(decisions.map(d => d.board).filter(Boolean))).sort();
  const [boardFilterInit, setBoardFilterInit] = React.useState(false);
  React.useEffect(() => {
    if (!boardFilterInit && availableBoards.length > 0) {
      setBoardFilterInit(true);
      setBoardFilter(new Set(availableBoards));
    }
  }, [boardFilterInit, availableBoards]);
  const visibleDecisions = decisions.filter(d => boardFilter.has(d.board));
  const allTechs = schedData?.items ?? [];
  const [techFilterInit, setTechFilterInit] = React.useState(false);
  React.useEffect(() => {
    if (!techFilterInit && allTechs.length > 0) {
      setTechFilterInit(true);
      try {
        const saved = sessionStorage.getItem(techFilterKey);
        if (saved) setTechFilter(new Set(JSON.parse(saved) as string[]));
        else setTechFilter(new Set(allTechs.map(t => t.techId)));
      } catch { setTechFilter(new Set(allTechs.map(t => t.techId))); }
    }
  }, [techFilterInit, allTechs]);
  const techs = React.useMemo(() => allTechs.filter(t => techFilter.has(t.techId)), [allTechs, techFilter]);
  const blocks = schedData?.blocks ?? [];
  const hours = Array.from({ length: 12 }, (_, i) => i + 7);

  function blocksForDay(techId: string) {
    return blocks.filter(b => {
      if (b.technicianId !== techId) return false;
      const s = new Date(b.startsAt), e = new Date(b.endsAt);
      return s < dayEnd && e > dayStart;
    });
  }

  async function accept(decisionId: string, techId: string) {
    setAccepting(techId);
    try {
      await apiPost('/api/v1/dispatch/' + decisionId + '/accept', { techId });
      setAccepted(prev => new Set([...prev, decisionId]));
      setSelected(null);
      refetchDispatch();
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Failed to accept');
    } finally { setAccepting(null); }
  }

  function onDropToLane(e: React.DragEvent<HTMLDivElement>, tech: ScheduleTech) {
    e.preventDefault();
    const decisionId = e.dataTransfer.getData('text/dispatch-decision');
    if (!decisionId || dropBusy) return;
    const decision = decisions.find(d => d.id === decisionId);
    if (!decision) return;
    const lane = e.currentTarget;
    const rect = lane.getBoundingClientRect();
    const frac = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 0.999);
    const snapped = Math.round((frac * 24 * 60) / 15) * 15;
    const minutesFromMidnight = Math.max(420, Math.min(1125, snapped));
    const startsAt = new Date(dayStart.getTime() + minutesFromMidnight * 60000).toISOString();
    const proposed = decision.candidates.find(c2 => c2.techId === tech.techId);
    let durationMinutes = 60;
    if (proposed?.proposedStart && proposed?.proposedEnd) {
      durationMinutes = Math.max(15, Math.min(480, Math.round((new Date(proposed.proposedEnd).getTime() - new Date(proposed.proposedStart).getTime()) / 60000)));
    }
    setDropBusy(tech.techId);
    apiPost('/api/v1/dispatch/' + decisionId + '/schedule', { techId: tech.techId, startsAt, durationMinutes })
      .then(() => {
        toast.success('Scheduled ' + tech.name + ' at ' + new Date(startsAt).toLocaleTimeString('en-US', { timeZone: 'America/Chicago', hour: 'numeric', minute: '2-digit' }));
        setAccepted(prev => new Set([...prev, decisionId]));
        setSelected(null);
        refetchDispatch();
        refetchSchedule();
      })
      .catch((err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to schedule'))
      .finally(() => setDropBusy(null));
  }

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* LEFT RAIL: Action Queue */}
      <aside className="w-80 shrink-0 border-r border-[hsl(var(--border))] flex flex-col bg-[hsl(var(--surface-subtle))]/40">
        <div className="px-4 py-3 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
          <h1 className="text-base font-bold text-[hsl(var(--content))]">Cockpit</h1>
          <p className="text-xs text-[hsl(var(--content-subtle))]">{visibleDecisions.length} awaiting dispatch
            {(boardFilter.size > 0 || availableBoards.length > 1) && (
              <span className='text-[10px] text-[hsl(var(--content-placeholder))]'>
                {boardFilter.size === 0 ? 'all boards' : boardFilter.size + ' of ' + availableBoards.length + ' boards'}
              </span>
            )} · drag onto a tech row to schedule</p>
        </div>
        {availableBoards.length > 1 && (
          <div className="relative px-2 pt-2">
            <button
              type='button'
              onClick={() => setBoardMenuOpen(o => !o)}
              className="w-full flex items-center justify-between rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-3 py-1.5 text-xs text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
              aria-haspopup='listbox'
              aria-expanded={boardMenuOpen}>
              <span className='truncate'>{boardFilter.size === 0 ? 'All boards' : boardFilter.size + ' selected'}</span>
              <span className='text-[hsl(var(--content-subtle))] text-[10px]' aria-hidden='true'>{boardMenuOpen ? '▲' : '▼'}</span>
            </button>
            {boardMenuOpen && (
              <div className="absolute left-2 right-2 top-full z-30 mt-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-lg p-1 max-h-56 overflow-y-auto"
                role='listbox'
                aria-multiselectable='true'>
                {boardViews.length > 0 && (
                  <div className='px-2 pt-1 pb-1 space-y-0.5 border-b border-[hsl(var(--border-subtle))] mb-1'>
                    <p className='px-1 text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))]'>Saved views</p>
                    {boardViews.map(v => (
                      <div key={v.name} className='flex items-center gap-1 px-1'>
                        <button type='button' onClick={() => applyBoardView(v)}
                          className='flex-1 text-left rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] truncate'>{v.name}</button>
                        <button type='button' onClick={() => deleteBoardView(v.name)} aria-label={'Delete ' + v.name}
                          className='text-[hsl(var(--content-subtle))] hover:text-red-500 text-[11px] px-1'>✕</button>
                      </div>
                    ))}
                  </div>
                )}
                {availableBoards.map(b => (
                  <label key={b} className="flex items-center gap-2 px-2 py-1.5 rounded-md text-xs text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] cursor-pointer">
                    <input
                      type='checkbox'
                      checked={boardFilter.has(b)}
                      onChange={() => setBoardFilter(prev => {
                        const next = new Set(prev);
                        if (next.has(b)) next.delete(b); else next.add(b);
                        return next;
                      })}
                      className='rounded border-[hsl(var(--border)]'
                    />
                    <span className='truncate'>{b}</span>
                  </label>
                ))}
                <div className='flex gap-1 px-2 pt-1 pb-0.5'>
                  <button type='button' onClick={() => setBoardFilter(new Set(availableBoards))}
                    className='flex-1 rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]'>All</button>
                  <button type='button' onClick={() => setBoardFilter(new Set())}
                    className='flex-1 rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]'>None</button>
                </div>
                <div className='flex gap-1 px-2 pt-1 pb-1 border-t border-[hsl(var(--border-subtle))] mt-1'>
                  <input type='text' value={boardViewName} onChange={(e) => setBoardViewName(e.target.value)} placeholder='View name…'
                    className='flex-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-[11px] text-[hsl(var(--content))] outline-none' />
                  <button type='button' onClick={saveBoardView} disabled={!boardViewName.trim()}
                    className='rounded-md bg-brand-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50'>Save</button>
                </div>
              </div>
            )}
          </div>
        )}
        <div className="flex-1 overflow-y-auto p-2 space-y-2">
          {dispatchLoading ? (
            Array.from({ length: 4 }).map((_, i) => <CardSkeleton key={i} lines={3} />)
          ) : decisions.length === 0 ? (
            <EmptyState icon="✅" title="Queue clear" description="No tickets awaiting dispatch decisions." />
          ) : (
            visibleDecisions.map(d => {
              const top = d.candidates[0];
              return (
                <div key={d.id} onClick={() => setSelected(d)}
                  draggable
                  onDragStart={(e) => { e.dataTransfer.setData('text/dispatch-decision', d.id); e.dataTransfer.effectAllowed = 'move'; }}
                  className={cx('rounded-xl border p-3 cursor-pointer transition-colors',
                    selected?.id === d.id
                      ? 'border-brand-400 bg-[hsl(var(--surface))] shadow-[inset_2px_0_0_0_hsl(var(--border-focus))]'
                      : 'border-[hsl(var(--border))] bg-[hsl(var(--surface))] hover:bg-[hsl(var(--surface))]')}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold font-mono text-[hsl(var(--content))]">#{d.ticketId}</span>
                    <span className={cx('text-[10px] px-1.5 py-0.5 rounded-full font-semibold', modeBadge(d.mode))}>{d.mode}</span>
                    <span className="text-[10px] text-[hsl(var(--content-placeholder))] ml-auto">{relativeTime(d.createdAt)}</span>
                  </div>
                  <p className="text-xs font-medium text-[hsl(var(--content))] line-clamp-2 mb-2">{d.ticketSummary}</p>
                  {d.candidates.slice(0, 2).map(c => (
                    <div key={c.techId} className="flex items-center gap-1.5 mb-1">
                      <span className={cx('text-[10px] font-bold px-1.5 py-0.5 rounded', rankBadge(c.rank))}>#{c.rank}</span>
                      <span className="text-[11px] text-[hsl(var(--content))] truncate flex-1">{c.techName}</span>
                      <span className="text-[10px] font-mono text-[hsl(var(--content-subtle))]">{Math.round(c.score * 100)}%</span>
                      {(c.skillScore ?? 0) < 0.25 && <AlertTriangle className="h-3 w-3 text-amber-500 shrink-0" aria-label="Low skill match" />}
                    </div>
                  ))}
                  {top && (
                    <button
                      onClick={(e) => { e.stopPropagation(); accept(d.id, top.techId); }}
                      disabled={accepting !== null || accepted.has(d.id)}
                      className="mt-2 w-full rounded-lg bg-brand-600 px-2 py-1.5 text-[11px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50 transition-colors">
                      {accepted.has(d.id) ? '✓ Assigned' : accepting === top.techId ? 'Assigning…' : 'Assign ' + top.techName.split(' ')[0]}
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </aside>

      {/* CENTER: technician skills (when a tech is selected in day/week calendar) */}
      {selectedTech && viewMode !== 'month' && (() => {
        const tech = (techSkillsData?.items ?? []).find(t => t.id === selectedTech.techId);
        const skills = (tech?.skills ?? []).slice().sort((a, b) => b.level - a.level);
        return (
        <aside className="w-80 shrink-0 border-l border-[hsl(var(--border))] bg-[hsl(var(--surface))] overflow-y-auto flex flex-col">
          <div className="sticky top-0 z-10 bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))] px-5 py-4 flex items-center justify-between">
            <div>
              <span className="text-sm font-bold text-[hsl(var(--content))]">{selectedTech.name}</span>
              <p className="text-[10px] uppercase tracking-wide text-[hsl(var(--content-subtle))]">Skills</p>
            </div>
            <button onClick={() => setSelectedTech(null)} aria-label="Close" className="text-[hsl(var(--content-subtle))] hover:text-[hsl(var(--content))]"><X className="h-4 w-4" /></button>
          </div>
          <div className="p-4 space-y-2">
            {skills.length === 0 ? (
              <p className="text-xs text-[hsl(var(--content-subtle))]">No skills recorded for this technician.</p>
            ) : skills.map(s => (
              <div key={s.skillId} className="rounded-lg border border-[hsl(var(--border-subtle))] bg-[hsl(var(--surface-subtle))] px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-[hsl(var(--content))] truncate">{s.skillName}</span>
                  <span className={'text-[10px] font-bold px-1.5 py-0.5 rounded shrink-0 ' + (s.level >= 4 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : s.level === 3 ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : s.level === 2 ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300')}>L{s.level}</span>
                </div>
                <p className="text-[10px] text-[hsl(var(--content-subtle))] mt-0.5">{s.category} · {s.source}</p>
              </div>
            ))}
          </div>
        </aside>
        );
      })()}
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
          <div className="flex items-center gap-2">
            <div className='flex rounded-lg border border-[hsl(var(--border))] overflow-hidden'>
              {(['day', 'week', 'month'] as const).map(v => (
                <button key={v} onClick={() => setViewMode(v)}
                  className={'px-2.5 py-1 text-[11px] font-semibold capitalize transition-colors ' +
                    (viewMode === v ? 'bg-brand-600 text-white' : 'text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]')}>
                  {v}
                </button>
              ))}
            </div>
            <button onClick={() => setDayOffset(d => d - 1)} className="h-7 w-7 flex items-center justify-center rounded-lg text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]"><ChevronLeft className="h-4 w-4" /></button>
            <span className="text-sm font-semibold text-[hsl(var(--content))] min-w-[10rem] text-center">
              {viewDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
            </span>
            <button onClick={() => setDayOffset(d => d + 1)} className="h-7 w-7 flex items-center justify-center rounded-lg text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]"><ChevronRight className="h-4 w-4" /></button>
            {dayOffset !== 0 && <button onClick={() => setDayOffset(0)} className="text-xs text-brand-600 hover:underline ml-1">Today</button>}
          </div>

          <div className="flex items-center gap-2">
            {viewMode !== 'month' && headerOncall ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-2.5 py-1 text-[11px] font-semibold">
                {"\ud83d\udcde"} {headerOncall.techName}
              </span>
            ) : viewMode !== 'month' ? (
              <div className='relative'>
                <button type='button' onClick={() => setOncallMenuOpen(o => !o)}
                  className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--border))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                  aria-haspopup='listbox' aria-expanded={oncallMenuOpen}>
                  {"\ud83d\udcde"} Set on-call {"\u25be"}
                </button>
                {oncallMenuOpen && (
                  <div className="absolute right-0 top-full z-30 mt-1 w-48 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-lg p-1 max-h-56 overflow-y-auto"
                    role='listbox'>
                    {techs.map(t => (
                      <button type='button' key={t.techId} onClick={() => assignOncall(t.techId)}
                        className="w-full text-left px-2.5 py-1.5 rounded-md text-xs text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] transition-colors">
                        {t.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : null}
            <div className="relative">
              <button type="button" onClick={() => setTechMenuOpen(o => !o)}
                className="inline-flex items-center gap-1 rounded-lg border border-[hsl(var(--border))] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))] transition-colors"
                aria-haspopup="listbox" aria-expanded={techMenuOpen}>
                {techFilter.size === 0 ? (techs.length + ' technicians') : (techFilter.size + ' of ' + (schedData?.items?.length ?? 0) + ' technicians')} ▾
              </button>
              {techMenuOpen && (
                <div className="absolute right-0 top-full z-30 mt-1 w-64 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--surface))] shadow-lg p-2 max-h-80 overflow-y-auto"
                  role="listbox">
                  {savedViews.length > 0 && (
                    <div className="pb-2 space-y-0.5 border-b border-[hsl(var(--border-subtle))] mb-2">
                      <p className="px-2 text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))]">Saved views</p>
                      {savedViews.map(v => (
                        <div key={v.name} className="flex items-center gap-1 px-2">
                          <button type="button" onClick={() => applySavedView(v)}
                            className="flex-1 text-left rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] truncate">{v.name}</button>
                          <button type="button" onClick={() => deleteSavedView(v.name)} aria-label={'Delete ' + v.name}
                            className="text-[hsl(var(--content-subtle))] hover:text-red-500 text-[11px] px-1">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="flex gap-1 pb-2 border-b border-[hsl(var(--border-subtle))]">
                    <button type="button" onClick={() => persistTechFilter(new Set((schedData?.items ?? []).map(t => t.techId)))}
                      className="flex-1 rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]">All</button>
                    <button type="button" onClick={() => persistTechFilter(new Set())}
                      className="flex-1 rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content-subtle))] hover:bg-[hsl(var(--surface-subtle))]">None</button>
                  </div>
                  <div className="py-1 space-y-0.5">
                    {(schedData?.items ?? []).map(t => (
                      <label key={t.techId} className="flex items-center gap-2 px-2 py-1 rounded-md text-xs text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] cursor-pointer">
                        <input type="checkbox" checked={techFilter.has(t.techId)}
                          onChange={() => {
                            const next = new Set(techFilter);
                            if (next.has(t.techId)) next.delete(t.techId); else next.add(t.techId);
                            persistTechFilter(next);
                          }}
                          className="rounded border-[hsl(var(--border))]" />
                        <span className="truncate">{t.name}</span>
                      </label>
                    ))}
                  </div>
                  {savedViews.length > 0 && (
                    <div className="mt-1 pt-2 border-t border-[hsl(var(--border-subtle))] space-y-0.5">
                      <p className="px-2 text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))]">Saved views</p>
                      {savedViews.map(v => (
                        <div key={v.name} className="flex items-center gap-1 px-2">
                          <button type="button" onClick={() => applySavedView(v)}
                            className="flex-1 text-left rounded-md px-2 py-1 text-[11px] text-[hsl(var(--content))] hover:bg-[hsl(var(--surface-subtle))] truncate">{v.name}</button>
                          <button type="button" onClick={() => deleteSavedView(v.name)} aria-label={'Delete ' + v.name}
                            className="text-[hsl(var(--content-subtle))] hover:text-red-500 text-[11px] px-1">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="mt-1 pt-2 border-t border-[hsl(var(--border-subtle))] flex gap-1">
                    <input type="text" value={viewName} onChange={(e) => setViewName(e.target.value)} placeholder="View name…"
                      className="flex-1 rounded-md border border-[hsl(var(--border))] bg-[hsl(var(--surface))] px-2 py-1 text-[11px] text-[hsl(var(--content))] outline-none" />
                    <button type="button" onClick={saveCurrentView} disabled={!viewName.trim()}
                      className="rounded-md bg-brand-600 px-2.5 py-1 text-[11px] font-semibold text-white hover:bg-brand-700 disabled:opacity-50">Save</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="min-h-0 overflow-auto">
          {viewMode === 'month' ? (
            <MonthGrid
              monthDate={viewDate}
              onPickDay={(d) => { const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime()) / 86400000); setDayOffset(diff); setViewMode('day'); }}
            />
          ) : viewMode === 'week' ? (
            schedLoading || techs.length === 0 ? (
              <p className="text-sm text-[hsl(var(--content-subtle))] p-4">Loading week...</p>
            ) : (
              <WeekGrid
              oncallSlots={oncallData?.items ?? []}
                techs={techs}
                blocks={blocks}
                weekStart={weekStart}
                decisions={decisions}
                onSchedule={(decisionId, techId, startsAt, durationMinutes) =>
                  apiPost('/api/v1/dispatch/' + decisionId + '/schedule', { techId, startsAt, durationMinutes })
                    .then(() => {
                      toast.success('Scheduled');
                      setAccepted(prev => new Set([...prev, decisionId]));
                      setSelected(null);
                      refetchDispatch();
                      refetchSchedule();
                    })
                    .catch((err: unknown) => toast.error(err instanceof Error ? err.message : 'Failed to schedule'))
                }
                onPickDay={(d) => { const diff = Math.round((new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime() - new Date(new Date().getFullYear(), new Date().getMonth(), new Date().getDate()).getTime()) / 86400000); setDayOffset(diff); setViewMode('day'); }}
              />
            )
          ) : schedLoading ? (
            <div className="p-4 space-y-2">{Array.from({ length: 5 }).map((_, i) => <CardSkeleton key={i} lines={1} />)}</div>
          ) : techs.length === 0 ? (
            <EmptyState icon="📅" title="No technicians" description="Sync technicians to see the schedule." />
          ) : (
            <div className="min-w-[640px]">
              <div className="sticky top-0 z-10 flex bg-[hsl(var(--surface))] border-b border-[hsl(var(--border))]">
                <div className="w-32 shrink-0 px-3 py-1.5 text-[10px] font-semibold uppercase text-[hsl(var(--content-subtle))] border-r border-[hsl(var(--border))]">Tech</div>
                {hours.map(h => (
                  <div key={h} className="flex-1 px-1 py-1.5 text-[10px] font-medium text-center text-[hsl(var(--content-subtle))] border-r border-[hsl(var(--border-subtle))]">
                    {h > 12 ? h - 12 : h}:00 {h >= 12 ? 'PM' : 'AM'}
                  </div>
                ))}
              </div>
              {techs.map(tech => (
                <div key={tech.techId}
                  className={'flex border-b border-[hsl(var(--border-subtle))] h-11 relative ' +
                    (isOnCallDuring(tech.techId, dayStart, dayEnd) ? 'shadow-[inset_3px_0_0_0_rgb(245_158_11)]' : '')}>
                  <div className="w-44 shrink-0 px-3 py-1.5 border-r border-[hsl(var(--border))] bg-[hsl(var(--surface))]">
                    <div className="flex items-center gap-1 min-w-0">
                      <button onClick={(e) => { e.stopPropagation(); setSelectedTech({ techId: tech.techId, name: tech.name }); }} className="text-xs font-medium text-[hsl(var(--content))] truncate hover:underline text-left">{tech.name}</button>
                      {isOnCallDuring(tech.techId, dayStart, dayEnd) && (
                        <span className="shrink-0 inline-flex items-center rounded-full bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 px-1.5 py-0.5 text-[9px] font-semibold">On-Call</span>
                      )}
                    </div>
                    {tech.role && <p className="text-[10px] text-[hsl(var(--content-subtle))] truncate">{tech.role}</p>}
                  </div>
                  <div
                    className="flex-1 relative"
                    onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }}
                    onDrop={(e) => onDropToLane(e, tech)}
                  >
                    {hours.map(h => (
                      <div key={h} className="absolute top-0 bottom-0 border-r border-[hsl(var(--border-subtle))]" style={{ left: ((h - 7) / 12) * 100 + '%' }} />
                    ))}
                    {blocksForDay(tech.techId).map((b, i) => {
                      const s = new Date(b.startsAt), e = new Date(b.endsAt);
                      const sC = Math.max(s.getTime(), dayStart.getTime());
                      const eC = Math.min(e.getTime(), dayEnd.getTime());
                      const left = ((sC - dayStart.getTime()) / 86400000) * 100;
                      const width = Math.max(((eC - sC) / 86400000) * 100, 1.5);
                      return (
                        <div key={i}
                          title={b.kind + ': ' + s.toLocaleTimeString('en-US', { timeZone: 'America/Chicago' }) + ' – ' + e.toLocaleTimeString('en-US', { timeZone: 'America/Chicago' })}
                          className={cx('absolute top-1 bottom-1 rounded-md border text-[9px] text-white overflow-hidden px-1 py-0.5', BLOCK_COLORS[b.kind] ?? 'bg-gray-500/70 border-gray-600')}
                          style={{ left: left + '%', width: width + '%' }}>
                          <span className="truncate block leading-tight">{b.kind}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="always-pane flex-1 min-h-0 border-t border-[hsl(var(--border))] bg-[hsl(var(--surface))] flex">
          <div className="w-3/4 min-w-0 h-full overflow-hidden">
            {selected ? (
              <TicketDetailPage embeddedId={selected.ticketCacheId ?? selected.ticketId} part="main" />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-[hsl(var(--content-subtle))]">
                Select a ticket in the queue to see details and notes.
              </div>
            )}
          </div>
          <div className="w-1/4 min-w-0 h-full border-l border-[hsl(var(--border))] overflow-hidden">
            {selected ? (
              <TicketDetailPage embeddedId={selected.ticketCacheId ?? selected.ticketId} part="sidebar" />
            ) : null}
          </div>
        </div>


      </div>
    </div>
  );
}
interface TicketDetail {
  cwId: number; summary: string; board: string; status: string; priority: string;
  company: string; contact: string; owner?: string; slaDeadline?: string;
  createdAt: string; updatedAt: string; estimatedHours?: number; aiReason?: string;
  sentiment?: number; unassigned?: boolean; slaRisk?: string;
}

function TicketDetailCard({ ticketCacheId, cwTicketId }: { ticketCacheId?: string; cwTicketId: string }) {
  const { data, loading, error } = useGet<TicketDetail>('/api/v1/tickets/' + (ticketCacheId ?? cwTicketId));
  if (loading) return <p className="text-xs text-[hsl(var(--content-subtle))] py-4">Loading ticket details…</p>;
  if (error || !data) return <p className="text-xs text-[hsl(var(--content-subtle))] py-4">Ticket details unavailable.</p>;
  const rows: Array<[string, React.ReactNode]> = [
    ['Status', <span key='s' className="font-medium text-[hsl(var(--content))]">{data.status}</span>],
    ['Priority', <span key='p' className="font-medium text-[hsl(var(--content))]">{data.priority}</span>],
    ['Company', <span key='c' className="text-[hsl(var(--content))]">{data.company}</span>],
    ['Contact', <span key='ct' className="text-[hsl(var(--content))]">{data.contact}</span>],
    ['Owner', <span key='o' className="text-[hsl(var(--content))]">{data.owner || 'Unassigned'}</span>],
    ['Est. hours', <span key='e' className="text-[hsl(var(--content))]">{data.estimatedHours != null ? data.estimatedHours + 'h' : '—'}</span>],
    ['SLA', data.slaDeadline ? <span key='sl' className={data.slaRisk === 'critical' ? 'text-red-500 font-medium' : data.slaRisk === 'warning' ? 'text-amber-500' : 'text-[hsl(var(--content))]'}>{new Date(data.slaDeadline).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</span> : <span key='sl'>—</span>],
    ['Created', <span key='cr' className="text-[hsl(var(--content-subtle))]">{new Date(data.createdAt).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</span>],
    ['Updated', <span key='u' className="text-[hsl(var(--content-subtle))]">{new Date(data.updatedAt).toLocaleString('en-US', { timeZone: 'America/Chicago' })}</span>],
  ];
  return (
    <div className="rounded-xl border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] p-4 space-y-3">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--content-subtle))]">Ticket Details</h4>
      <dl className="space-y-1.5">
        {rows.map(([label, node]) => (
          <div key={String(label)} className="flex items-baseline justify-between gap-3">
            <dt className="text-[11px] text-[hsl(var(--content-subtle))] shrink-0">{label}</dt>
            <dd className="text-xs text-right">{node}</dd>
          </div>
        ))}
      </dl>
      {data.aiReason && (
        <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2">
          <p className="text-[10px] font-semibold text-blue-700 dark:text-blue-300 mb-0.5">AI Triage</p>
          <p className="text-[11px] text-blue-800 dark:text-blue-200">{data.aiReason}</p>
        </div>
      )}
      {data.sentiment != null && (
        <p className="text-[10px] text-[hsl(var(--content-subtle))]">Sentiment: {data.sentiment}/5</p>
      )}
    </div>
  );
}

interface TicketNoteItem { id: string; content: string; internal?: boolean; createdAt: string; author?: string }
